import { useState } from 'react';

import { BulkImportLocationsRequestDTO } from '@gearbelt/global';
import { Button, buttonVariants } from '@gearbelt/ui/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { z } from 'zod';

import DeleteIcon from '@/components/icons/delete';
import QuestionIcon from '@/components/icons/question';
import UploadIcon from '@/components/icons/upload';
import { TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Dropzone, DropzoneContent, DropzoneDescription, DropzoneInput, DropzoneTitle } from '@/components/ui/dropzone';
import { useToast } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { locationKeys } from '@/queries/locations';
import { ApiService } from '@/services/api-v2';

const locationSheetImportSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  address: z.string().optional(),
  lat: z.string().optional(),
  lng: z.string().optional(),
  radius: z.number().int().positive().optional(),
  zoom: z.number().int().positive().optional(),
  emails: z.array(z.string().email()).optional(),
});

export type LocationSheetImportType = z.infer<typeof locationSheetImportSchema>;

export default function BulkLocationImportDialog({
  open: openProp,
  onOpenChange,
  noTrigger = false,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean, success?: boolean) => void;
  noTrigger?: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(openProp ?? false);
  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState<LocationSheetImportType[]>([]);

  const { mutateAsync: bulkImportLocations, isPending: bulkImportLocationsPending } = useMutation({
    mutationFn: (values: LocationSheetImportType[]) => {
      return ApiService.BulkImportLocations({
        locations: values.map<BulkImportLocationsRequestDTO['locations'][number]>((location) => ({
          name: location.name,
          address: location.address,
          lat: location.lat,
          lng: location.lng,
          radius: location.radius,
          zoom: location.zoom,
          emails: location.emails,
        })),
      });
    },
    onSuccess: async () => {
      await queryClient.resetQueries({
        queryKey: locationKeys.all,
      });
      handleOpenChange(false, true);
      toast.success('Locations imported successfully');
    },
    onError: (error) => {
      toast.error(`Error importing locations: ${error.message}`);
    },
  });

  const handleOpenChange = (open: boolean, success?: boolean) => {
    onOpenChange?.(open, success);
    setOpen(open);

    if (!open) {
      setFile(null);
      setData([]);
    }
  };

  const locationSheetSchema = z.object({
    Name: z.coerce.string({ message: 'Name is required.' }).trim().min(1, 'Name is required.'),
    Address: z.coerce
      .string()
      .optional()
      .transform((val) => val?.trim()),
    Lat: z.coerce
      .string()
      .optional()
      .transform((val) => val?.trim()),
    Lng: z.coerce
      .string()
      .optional()
      .transform((val) => val?.trim()),
    Radius: z.coerce.number().int('Radius must must be a whole number').positive('Radius must be positive').optional(),
    Zoom: z.coerce.number().int('Zoom must be a whole number').positive('Zoom must be positive').optional(),
    Emails: z
      .string()
      .optional()
      .transform((val) =>
        val
          ? val
              .trim()
              .split(',')
              .map((email) => email.trim())
              .filter((email) => email.length > 0)
          : [],
      ),
  });

  const handleImport = (file: File) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          if (event.target && event.target.result) {
            const data = new Uint8Array(event.target.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array', raw: true });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];

            const expectedColumns = Object.keys(locationSheetSchema.shape);
            const sheetColumns = Object.keys(XLSX.utils.sheet_to_json(sheet, { defval: null })[0] || {});
            const missingColumns = expectedColumns.filter((col) => !sheetColumns.includes(col));

            if (missingColumns.length > 0) {
              toast.error(`File has missing columns ${missingColumns.join(', ')}. Please use the Excel template provided.`);
              return;
            }

            const jsonData = XLSX.utils.sheet_to_json(sheet, { raw: true, rawNumbers: false });

            const parsedItems: z.infer<typeof locationSheetSchema>[] = [];
            const uniqueNames = new Map<string, number>();

            for (const [idx, item] of jsonData.entries()) {
              const parsedItem = locationSheetSchema.safeParse(item);
              if (!parsedItem.success) {
                const errorIssue = parsedItem.error.issues[0];
                const errorMessage =
                  errorIssue.code === 'invalid_type' || errorIssue.code === 'custom'
                    ? `Row #${idx + 1 + 1}: ${errorIssue.message}.`
                    : `Row #${idx + 1 + 1} is missing the required field "${errorIssue.path[0]?.toString()}".`;
                toast.error(errorMessage);
                return;
              }

              const name = parsedItem.data.Name.toLowerCase();
              const firstAppearanceIndex = uniqueNames.get(name);
              if (firstAppearanceIndex !== undefined) {
                toast.error(`Location name "${parsedItem.data.Name}" must be unique, first appeared on Row #${firstAppearanceIndex + 1 + 1}`);
                return;
              }
              uniqueNames.set(name, idx);

              parsedItems.push(parsedItem.data);
            }

            const locationsToImport = parsedItems.map((location) => ({
              name: location.Name,
              address: location.Address,
              lat: location.Lat,
              lng: location.Lng,
              radius: location.Radius,
              zoom: location.Zoom,
              emails: location.Emails,
            }));

            const validationResult = z.array(locationSheetImportSchema).safeParse(locationsToImport);
            if (!validationResult.success) {
              const errorPath = validationResult.error.issues[0].path;
              const rowIndex = typeof errorPath[0] === 'number' ? errorPath[0] : 0;
              toast.error(`Row #${rowIndex + 2} contains invalid values or formatting. Please use the Excel template provided.`);
              return;
            }

            setData(validationResult.data);
            setFile(file);
          }
        } catch (error) {
          toast.error(`Failed to parse file: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  async function handleSubmit() {
    await bulkImportLocations(data);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!noTrigger && (
        <DialogTrigger asChild>
          <TablePageHeaderActionButton icon={<UploadIcon />} label="Upload Locations" />
        </DialogTrigger>
      )}
      <DialogContent className="flex flex-col gap-4">
        <VisuallyHidden>
          <DialogDescription>
            To bulk import your locations, please use the Excel template provided below. <b>Name</b> is the only required field, everything else is optional.
          </DialogDescription>
        </VisuallyHidden>

        <DialogTitle>Upload Locations</DialogTitle>

        <div>
          <div className="text-sm">
            To bulk import your locations, please use the Excel template provided below. <b>Name</b> is the only required field, everything else is optional.
            <br />
            <br />
            It is important that the columns match this format exactly. Cells that are empty or have incorrect formatting will be ignored.
            <br />
            <br />
            <a href="/gearbelt-location-import-template.xlsx" download className="text-default-blue underline">
              Download Excel template
            </a>
          </div>

          <div className="flex flex-col gap-2">
            <Link
              to="https://intercom.help/dashpivot/en/articles/10094910-how-to-bulk-import-locations-to-gearbelt"
              target="_blank"
              className={cn(buttonVariants({ variant: 'grey', size: 'sm' }), 'ml-auto flex items-center gap-1')}>
              <QuestionIcon className="h-4 w-4" />
              Learn how to bulk import
            </Link>

            <Dropzone
              accept={{
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                'application/vnd.ms-excel': ['.xls'],
                'text/csv': ['.csv'],
              }}
              maxSize={10 * 1024 * 1024}
              maxFiles={1}
              onDropAccepted={(files) => handleImport(files[0])}
              onDropRejected={(fileRejections) => {
                const rejection = fileRejections[0];
                if (rejection.errors.some((e) => e.code === 'file-invalid-type')) {
                  toast.error('File type is not supported.');
                } else if (rejection.errors.some((e) => e.code === 'file-too-large')) {
                  toast.error('File exceeds 10MB.');
                } else {
                  toast.error('File is invalid. Please check the file and try again.');
                }
              }}>
              <DropzoneContent
                className={cn(
                  'border-grey-4 flex w-full items-center justify-center border-2',
                  file ? 'cursor-default border-solid' : 'bg-grey-1 border-dashed',
                )}>
                {file === null ? (
                  <>
                    <DropzoneTitle>
                      <UploadIcon className="text-secondary-text size-8" />
                    </DropzoneTitle>
                    <DropzoneDescription>Drag and drop your files here or click to upload</DropzoneDescription>
                    <DropzoneInput />
                  </>
                ) : (
                  <div className="flex w-full items-center justify-between gap-2 text-sm">
                    <div>{file.name}</div>
                    <DeleteIcon className="text-default-red size-4 cursor-pointer" onClick={() => setFile(null)} />
                  </div>
                )}
              </DropzoneContent>
            </Dropzone>

            <div className="text-secondary-text text-sm">
              <div>Accepted file types: .xlsx, .xls, .csv</div>
              <div>File size limit: 10MB</div>
            </div>
          </div>
        </div>

        <Button variant="success" className="w-fit" disabled={bulkImportLocationsPending || data.length === 0} onClick={handleSubmit}>
          Import
        </Button>
      </DialogContent>
    </Dialog>
  );
}
